import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, Input, OnInit } from '@angular/core';
import { RoutingConfig } from '@app/routing/routing.config';
import { Router } from '@angular/router';
import { DevicesClass, DeviceTypes } from '@app/core/classes/component/devices.class';
import { AccountEventBus, AccountStoryService } from '@app/modules/account-modules/account.story';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IAccountUserInfo } from '@app/modules/account-modules/account.model';
import { MenuItem, PrimeNGConfig } from 'primeng/api';
import { NotificationStoryService, NotificationEventBus } from '@app/modules/notification-modules/notification.story';
import { AppStaticURL } from '@app/app.settings';
import { RolesService } from '@app/core/services';

@UntilDestroy()
@Component({
  selector: 'shared-account-header',
  templateUrl: 'account-header.component.html',
  styleUrls: [`./account-header.component.scss`],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class AccountHeaderComponent extends DevicesClass implements OnInit {
  public StaticURL = AppStaticURL;
  public Routing = RoutingConfig;

  /**
   * Base components variables
   */
  public AccountHeaderDisplay: boolean;
  public AccountHeaderUser: IAccountUserInfo = null;
  public AccountHeaderMenu: MenuItem[] = this.onAccountHeaderMenu();
  public AccountHeaderNotify: number = 0;

  /**
   * Static URL.
   */
  public SignInURL: string = `/${RoutingConfig.Login}/${RoutingConfig.SignIn}`;
  public SignUpURL: string = `/${RoutingConfig.Login}/${RoutingConfig.SignUp}`;

  @Input() Device: DeviceTypes;

  constructor(injector: Injector,
    public cdr: ChangeDetectorRef,
    private router: Router,
    private primengConfig: PrimeNGConfig,
    private accountStory: AccountStoryService,
    private notificationStory: NotificationStoryService,
    private rolesService: RolesService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.primengConfig.ripple = true;

    this.onAccountStoryBus();
    this.onNotificationStoryBus();
  }

  onAccountStoryBus(): void {
    this.accountStory.AccountEventBus$.pipe(untilDestroyed(this)).subscribe(
      emit => this.onAccountStoryListener(emit)
    );
  }

  onAccountStoryListener(emit: AccountEventBus): void {
    if (emit.event === 'onAccountUpdate') {
      this.AccountHeaderUser = emit.data ? emit.data : null;
      this.AccountHeaderDisplay = true;
      this.AccountHeaderMenu = this.onAccountHeaderMenu();
      this.cdr.markForCheck();
    }

    if (emit.event === 'onAccountLogout' || emit.event === 'onAccountEmpty') {
      this.AccountHeaderUser = null;
      this.AccountHeaderDisplay = true;
    }

    this.cdr.markForCheck();
  }

  onNotificationStoryBus(): void {
    this.notificationStory.NotificationEventBus$.pipe(untilDestroyed(this)).subscribe(
      emit => this.onNotificationStoryListener(emit)
    );
  }

  onNotificationStoryListener(emit: NotificationEventBus): void {
    if (emit.event === 'onUpdateCount') {
      this.AccountHeaderNotify = emit.data;
      this.AccountHeaderMenu = this.onAccountHeaderMenu();
      this.cdr.markForCheck();
    }
  }

  onAccountLogOut(): void {
    this.accountStory.AccountEventBus.next({
      event: 'onAccountLogout'
    });
  }

  onAccountErrorPicPath(): void {
    this.AccountHeaderUser.picPath = null;
    this.cdr.markForCheck();
  }

  onSignInURL($event: Event): void {
    $event.preventDefault();

    this.onCheckLoginURL() || location.pathname === '/' ?
      this.router.navigateByUrl(`${this.SignInURL}`) :
      this.router.navigateByUrl(`${this.SignInURL}?redirectUrl=${location.pathname}`);
  }

  onSignUpURL($event: Event): void {
    $event.preventDefault();

    this.onCheckLoginURL() || location.pathname === '/' ?
      this.router.navigateByUrl(`${this.SignUpURL}`) :
      this.router.navigateByUrl(`${this.SignUpURL}?redirectUrl=${location.pathname}`);
  }

  onCheckLoginURL(): boolean {
    return location.pathname.split('/').some((item) => {
      return (
        item === RoutingConfig.Login ||
        item === RoutingConfig.PageErrors
      );
    });
  }

  onAccountMenuNotify(): string {
    return this.AccountHeaderNotify > 0 ? '(' + this.AccountHeaderNotify + ')' : '';
  }

  onAccountHeaderMenu(): Array<MenuItem> {
    if (this.onAdminRole) {
      return [{
        label: 'Меню профиля',
        items: [
          {
            label: 'Пользователи',
            icon: 'pi pi-user',
            routerLink: `/${RoutingConfig.Admin}/${RoutingConfig.AdminUsers}`
          },
          {
            separator: true
          },
          {
            label: 'Выход',
            icon: 'pi pi-fw pi-power-off',
            command: () => this.onAccountLogOut()
          },
        ]
      }]
    }

    if (this.onModeratorRole) {
      return [{
        label: 'Меню профиля',
        items: [
          {
            separator: true
          },
          {
            label: 'Выход',
            icon: 'pi pi-fw pi-power-off',
            command: () => this.onAccountLogOut()
          },
        ]
      }]
    }

    if (this.onUserRole) {
      return [{
        label: 'Меню профиля',
        items: [
          {
            label: 'Профиль',
            icon: 'pi pi-user',
            routerLink: `/${RoutingConfig.Account}/${RoutingConfig.AccountUser}`
          },
          {
            label: `Уведомления ${this.onAccountMenuNotify()}`,
            icon: 'pi pi-bell',
            routerLink: `/${RoutingConfig.Account}/${RoutingConfig.AccountNotify}`
          },
          {
            label: 'Избранное',
            icon: 'pi pi-star-o',
            routerLink: `/${RoutingConfig.Account}/${RoutingConfig.AccountFavorites}`
          },
          {
            label: 'Мои объявления',
            icon: 'pi pi-ticket',
            routerLink: `/${RoutingConfig.Account}/${RoutingConfig.AccountBulletins}`
          },
          {
            label: 'Карта музыканта',
            icon: 'pi pi-id-card',
            routerLink: `/${RoutingConfig.Account}/${RoutingConfig.AccountPoints}`
          },
          {
            separator: true
          },
          {
            label: 'Выход',
            icon: 'pi pi-fw pi-power-off',
            command: () => this.onAccountLogOut()
          },
        ]
      }]
    }

    if (this.onPartnerRole) {
      return [{
        label: 'Меню профиля',
        items: [
          {
            label: 'Профиль',
            icon: 'pi pi-user',
            routerLink: `/${RoutingConfig.Account}/${RoutingConfig.AccountPartner}`
          },
          // {
          //   label: `Уведомления ${this.onAccountMenuNotify()}`,
          //   icon: 'pi pi-bell',
          //   routerLink: `/${RoutingConfig.Account}/${RoutingConfig.AccountNotify}`
          // },
          {
            label: 'Статистика',
            icon: 'pi pi-chart-bar',
            routerLink: `/${RoutingConfig.Account}/${RoutingConfig.AccountStatistics}`
          },
          {
            label: 'Товары и услуги',
            icon: 'pi pi-money-bill',
            routerLink: `/${RoutingConfig.Account}/${RoutingConfig.AccountProducts}`
          },
          {
            label: 'Настройки',
            icon: 'pi pi-cog',
            routerLink: `/${RoutingConfig.Account}/${RoutingConfig.AccountManagement}`
          },
          {
            label: 'Отсканировать QR-код',
            icon: 'pi pi-link',
            routerLink: `/${RoutingConfig.Account}/${RoutingConfig.AccountQRScan}`
          },
          {
            separator: true
          },
          {
            label: 'Выход',
            icon: 'pi pi-fw pi-power-off',
            command: () => this.onAccountLogOut()
          },
        ]
      }]
    }
  }

  get onAdminRole(): boolean {
    return this.rolesService.onAdminRole(this.AccountHeaderUser) || false;
  }

  get onModeratorRole(): boolean {
    return this.rolesService.onModeratorRole(this.AccountHeaderUser) || false;
  }

  get onUserRole(): boolean {
    return this.rolesService.onUserRole(this.AccountHeaderUser) || false;
  }

  get onPartnerRole(): boolean {
    return this.rolesService.onPartnerRole(this.AccountHeaderUser) || false;
  }
}
